import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ReportPeriod,
  REPORTING_POLICY,
  reportingTimeBucket,
  resolveReportWindow,
} from '../../contracts/reporting-policy.generated';
import {
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonTitle,
  IonToolbar,
  IonButtons,
  ModalController,
} from '@ionic/angular/standalone';

export interface OperationsMetricFilterData {
  reportYear?: string;
  category?: string;
  severity?: string;
  metricType?: string;
  scopeLabel?: string;
  preset?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  sortMode?: string;
  currency?: string;
}

export interface OperationsMetricStatusOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-operations-metric-filter-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonFooter,
    IonGrid,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonRadio,
    IonRadioGroup,
    IonRow,
    IonTitle,
    IonToolbar,
  ],
  templateUrl: './operations-metric-filter-modal.component.html',
  styleUrls: ['./operations-metric-filter-modal.component.scss'],
})
export class OperationsMetricFilterModalComponent implements OnInit {
  private modalCtrl = inject(ModalController);

  @Input() filters?: OperationsMetricFilterData | null;
  @Input() title = 'Filters';
  @Input() showReportYear = false;
  @Input() statusLabel = 'Status';
  reportYear = '';
  @Input() statusOptions: OperationsMetricStatusOption[] = [];
  /** Opt-in domain facets; empty options preserve every existing report payload. */
  @Input() categoryOptions: OperationsMetricStatusOption[] = [];
  @Input() severityOptions: OperationsMetricStatusOption[] = [];
  category = 'all';
  severity = 'all';
  @Input() sortOptions: OperationsMetricStatusOption[] = [];
  sortMode = 'newest';
  @Input() currencyOptions: OperationsMetricStatusOption[] = [];
  currency = '';
  @Input() presetOptions?: OperationsMetricStatusOption[];
  @Input() defaultPreset = 'last30';
  @Input() showDateRange = true;
  @Input() showCustomRange = true;

  preset = 'last30';
  status = 'all';
  startDate = '';
  endDate = '';

  readonly defaultPresets = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'last90', label: 'Last 90 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' },
  ];

  get presets(): OperationsMetricStatusOption[] {
    const options = this.presetOptions?.length ? this.presetOptions : this.defaultPresets;
    return this.showCustomRange ? options : options.filter((option) => option.value !== 'custom');
  }

  ngOnInit() {
    this.reportYear = this.filters?.reportYear || '';
    this.preset = this.filters?.preset || this.defaultPreset;
    this.status = this.filters?.status || 'all';
    this.category = this.filters?.category || 'all';
    this.severity = this.filters?.severity || 'all';
    this.sortMode = this.filters?.sortMode || 'newest';
    this.currency = this.filters?.currency || '';
    const range = this.resolveRange(this.preset);
    this.startDate = this.filters?.startDate || range.startDate;
    this.endDate = this.filters?.endDate || range.endDate;
  }

  onPresetChange(value: string) {
    this.preset = value;
    if (value !== 'custom') {
      const range = this.resolveRange(value);
      this.startDate = range.startDate;
      this.endDate = range.endDate;
    }
  }

  clear() {
    this.modalCtrl.dismiss({ clear: true });
  }

  apply() {
    this.modalCtrl.dismiss({
      filters: {
        ...(this.filters?.metricType
          ? { metricType: this.filters.metricType, scopeLabel: this.filters.scopeLabel }
          : {}),
        ...(this.showReportYear ? { reportYear: String(this.reportYear || '') } : {}),
        preset: this.preset,
        startDate: this.startDate,
        endDate: this.endDate,
        status: this.status,
        ...(this.categoryOptions.length ? { category: this.category } : {}),
        ...(this.severityOptions.length ? { severity: this.severity } : {}),
        ...(this.sortOptions.length ? { sortMode: this.sortMode } : {}),
        ...(this.currencyOptions.length ? { currency: this.currency } : {}),
      } satisfies OperationsMetricFilterData,
    });
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  private resolveRange(preset: string): { startDate: string; endDate: string } {
    const asOf = Date.now();
    const period = preset === 'month' ? 'thisMonth' : preset;
    if (period === 'all' || period === 'asOf') return { startDate: '', endDate: '' };
    // Legacy "This Week" keeps its Monday-based meaning through the canonical bucket.
    if (period === 'week') {
      const bucket = reportingTimeBucket(asOf, 'week');
      return {
        startDate: new Date(bucket.startInclusive).toISOString().slice(0, 10),
        endDate: new Date(asOf).toISOString().slice(0, 10),
      };
    }
    const window = resolveReportWindow(
      {
        period:
          REPORTING_POLICY.periods.includes(period as ReportPeriod) && period !== 'custom'
            ? (period as ReportPeriod)
            : 'last30',
        timezone: 'UTC',
      },
      asOf
    );
    return {
      startDate: new Date(window.startInclusive!).toISOString().slice(0, 10),
      endDate: new Date(window.endExclusive - 1).toISOString().slice(0, 10),
    };
  }
}
